import React from 'react';
import {
  Drawer, Box, Typography, Stack, Chip, Divider, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/** Side drawer that renders the response of POST /hydration/describe.
 * Shape: { targets: { <target>: { family, fields, example, sources, ... } } }
 *
 * Helps the user see "what they'll get" before paying for a real source lookup.
 */
const HydrationPreviewDrawer = ({ open, onClose, data }) => {
  const targets = data?.targets || {};
  const targetNames = Object.keys(targets);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', md: 480 } } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Hydration preview</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Schema-only — no source lookups were performed.
        </Typography>

        {targetNames.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No targets in the spec — nothing to preview.
          </Typography>
        ) : targetNames.map((name) => {
          const t = targets[name];
          return (
            <Box key={name} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                  _source.metadata.{name}
                </Typography>
                <Chip size="small" label={t.family} variant="outlined" />
                <Chip size="small" label={`limit ${t.limit}`} variant="outlined" />
                <Chip size="small" label={t.merge} variant="outlined" />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Sources: {(t.sources || []).map((s) => `${s.plugin}(${s.share})`).join(' · ')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Fields:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {(t.fields || []).map((f) => (
                  <Chip key={f} size="small" label={f}
                        sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Example record:
              </Typography>
              <Box component="pre" sx={{
                p: 1, backgroundColor: 'action.hover', borderRadius: 1,
                fontSize: 11, overflow: 'auto', maxHeight: 240, m: 0,
              }}>
                {JSON.stringify(t.example || {}, null, 2)}
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
};

export default HydrationPreviewDrawer;
